import { makeCover, makeStones, SPAWNS } from "../content/map";
import { HERO_ORDER } from "../content/classes";
import { combat } from "../content/tuning";
import {
  finishCasts,
  resolveAutoTarget,
  resolvePos,
  tickCharges,
  tickDelays,
  tickProjectiles,
  tickPulses,
  tickZones,
  tryActivate,
} from "./abilities";
import { aiInput } from "./ai";
import { nextId, tickUnitClock } from "./combat";
import { checkWinner, tickScore, tickStones } from "./mode";
import { tickPassives } from "./passives";
import { canSee, isActable, isPresent, moveSpeed } from "./query";
import { spawnSpirit, makeUnit, pruneDeadSummons } from "./summon";
import {
  MATCH_SECONDS,
  type ClassId,
  type HeroClassId,
  type PlayerInput,
  type TeamId,
  type Unit,
  type World,
} from "./types";
import { ang, clamp, dist, fromAngle, sub, vec } from "./vec";

export function createEmptyWorld(seed = 1): World {
  return {
    time: 0,
    duration: MATCH_SECONDS,
    units: [],
    cover: [],
    stones: [],
    projectiles: [],
    pulses: [],
    zones: [],
    delays: [],
    events: [],
    nextId: 1,
    scores: [0, 0],
    over: false,
    winner: null,
    suddenDeath: false,
    rngState: seed >>> 0 || 1,
    endedReason: "",
    playerId: -1,
    hookDepth: 0,
  };
}

export interface AddUnitOpts {
  team: TeamId;
  name: string;
  classId: ClassId;
  pos: { x: number; y: number };
  isPlayer?: boolean;
  facing?: number;
}

export function addUnit(world: World, opts: AddUnitOpts): Unit {
  return makeUnit(world, opts);
}

export function populateMap(world: World): void {
  const nid = () => nextId(world);
  world.cover = makeCover(nid);
  world.stones = makeStones(nid);
}

export interface MatchOpts {
  classId: HeroClassId;
  seed?: number;
}

const ALLY_NAMES = ["Bramble", "Ione"];
const ENEMY_NAMES = ["Kesh", "Vellum", "Orin"];

function complement(pick: HeroClassId, avoid: HeroClassId[]): HeroClassId {
  const pref: HeroClassId[] = ["priest", "warrior", "ranger", "paladin", "assassin", "paragon", "cultist", "psychic", "dervish", "ritualist"];
  for (const id of pref) {
    if (id !== pick && !avoid.includes(id)) return id;
  }
  return HERO_ORDER.find((id) => id !== pick && !avoid.includes(id)) ?? "warrior";
}

export function createMatch(opts: MatchOpts): World {
  const world = createEmptyWorld(opts.seed ?? 1);
  populateMap(world);

  const a1 = complement(opts.classId, [opts.classId]);
  const a2 = complement(opts.classId, [opts.classId, a1]);
  const e1 = complement(a1, [opts.classId]);
  const e2 = complement(e1, [opts.classId, a1]);
  const e3 = complement(e2, [opts.classId, a1, e1]);

  const team0: AddUnitOpts[] = [
    { team: 0, name: "You", classId: opts.classId, pos: SPAWNS[0][1], isPlayer: true },
    { team: 0, name: ALLY_NAMES[0], classId: a1, pos: SPAWNS[0][0] },
    { team: 0, name: ALLY_NAMES[1], classId: a2, pos: SPAWNS[0][2] },
  ];
  const team1: AddUnitOpts[] = [
    { team: 1, name: ENEMY_NAMES[0], classId: e1, pos: SPAWNS[1][0] },
    { team: 1, name: ENEMY_NAMES[1], classId: e2, pos: SPAWNS[1][1] },
    { team: 1, name: ENEMY_NAMES[2], classId: e3, pos: SPAWNS[1][2] },
  ];
  for (const o of team0) addUnit(world, o);
  for (const o of team1) addUnit(world, o);
  return world;
}

function applyInput(world: World, unit: Unit, input: PlayerInput, dt: number): void {
  if (!isActable(unit) && unit.kind !== "spirit") return;

  if (input.clearTarget) unit.autoTargetId = null;
  if (input.clickedId != null) unit.autoTargetId = input.clickedId;

  const aimX = Number.isFinite(input.aimX) ? input.aimX : unit.pos.x + 1;
  const aimY = Number.isFinite(input.aimY) ? input.aimY : unit.pos.y;

  let mx = clamp(Number.isFinite(input.moveX) ? input.moveX : 0, -1, 1);
  let my = clamp(Number.isFinite(input.moveY) ? input.moveY : 0, -1, 1);
  const mag = Math.hypot(mx, my);
  if (mag > 1) {
    mx /= mag;
    my /= mag;
  }
  const movingNow = mx !== 0 || my !== 0;

  if (canMove(unit) && movingNow) {
    const spd = moveSpeed(unit);
    if (spd > 0) {
      const next = resolvePos(world, unit.pos.x + mx * spd * dt, unit.pos.y + my * spd * dt, unit.radius, unit.id);
      unit.pos.x = next.x;
      unit.pos.y = next.y;
    }
  }
  if (!unit.charging || unit.charging.remaining > 0) {
    const focus =
      unit.autoTargetId != null
        ? (world.units.find((u) => u.id === unit.autoTargetId && isPresent(u)) ?? null)
        : resolveAutoTarget(world, unit);
    if (focus) unit.facing = ang(sub(focus.pos, unit.pos));
    else if (movingNow) unit.facing = ang(vec(mx, my));
  }

  if (unit.kind === "spirit") return;
  if (input.skill) tryActivate(world, unit, "skill", aimX, aimY);
  if (input.ult) tryActivate(world, unit, "ult", aimX, aimY);
  if (combat.autoEnabled && !(combat.autoRequiresStill && movingNow)) {
    const t = resolveAutoTarget(world, unit);
    if (t) tryActivate(world, unit, "auto", t.pos.x, t.pos.y);
  }
}

function canMove(unit: Unit): boolean {
  if (unit.charging && unit.charging.remaining <= 0 && unit.charging.kind === "line") return false;
  if (unit.lockout > 0 && unit.statuses.some((s) => s.id === "stun")) return false;
  return isActable(unit);
}

export function tick(world: World, dt: number, inputs: PlayerInput[] = []): void {
  if (world.over) return;
  if (!Number.isFinite(dt) || dt < 0) dt = 0;
  dt = Math.min(dt, 0.05);
  world.time += dt;

  for (const e of world.events) e.age += dt;
  world.events = world.events.filter((e) => e.age < 0.95);
  tickPulses(world, dt);
  tickZones(world, dt);
  tickDelays(world, dt);

  for (const unit of world.units) {
    unit.prevPos = { x: unit.pos.x, y: unit.pos.y };
    tickUnitClock(world, unit, dt);
    tickPassives(world, unit, dt);
    if (unit.state.spawnSpirit === 1) {
      unit.state.spawnSpirit = 0;
      spawnSpirit(world, unit, unit.pos.x + (unit.team === 0 ? 0.8 : -0.8), unit.pos.y);
    }
  }

  finishCasts(world);
  tickCharges(world, dt);

  const byId = new Map<number, PlayerInput>();
  for (const inp of inputs) byId.set(inp.unitId, inp);

  for (const unit of world.units) {
    if (!isActable(unit) && unit.kind !== "spirit") continue;
    const given = byId.get(unit.id);
    const inp = given ?? aiInput(world, unit);
    applyInput(world, unit, inp, dt);
  }

  for (const unit of world.units) {
    const moved = dist(unit.pos, unit.prevPos) > 0.012;
    unit.movedThisTick = moved;
    if (moved) {
      unit.lastMoveAt = world.time;
      unit.stillFor = 0;
      const smoke = world.zones.some((z) => z.kind === "smoke" && (z.ownerId === unit.id || z.team === unit.team) && dist(z.pos, unit.pos) <= z.radius);
      if (unit.statuses.some((s) => s.id === "invisible") && !(unit.classId === "assassin" && smoke)) {
        unit.statuses = unit.statuses.filter((s) => s.id !== "invisible");
      }
    } else {
      unit.stillFor += dt;
    }
  }

  tickProjectiles(world, dt);
  updateVision(world);
  tickStones(world, dt);
  tickScore(world, dt);
  pruneDeadSummons(world);
  checkWinner(world);
}

function updateVision(world: World): void {
  for (const viewer of world.units) {
    for (const target of world.units) {
      if (viewer.id === target.id) continue;
      const wasUnseen = (viewer.seenAt[target.id] === undefined ? 999 : world.time - viewer.seenAt[target.id]) >= 1;
      if (canSee(world, viewer, target)) {
        if (wasUnseen && viewer.seenAt[target.id] !== undefined) {
          viewer.ambushGrace[target.id] = 0.45;
        }
        viewer.seenAt[target.id] = world.time;
      }
    }
  }
}

export function idleInput(unit: Unit): PlayerInput {
  return {
    unitId: unit.id,
    moveX: 0,
    moveY: 0,
    aimX: unit.pos.x + fromAngle(unit.facing).x,
    aimY: unit.pos.y + fromAngle(unit.facing).y,
    skill: false,
    ult: false,
    clickedId: null,
    clearTarget: false,
  };
}


